/* @flow */

import { cmsBlock } from "queries";

export const PAGE_SIZE = 32;
export const CAMPAIGN_CARD_INDEX = 6;
export const CAMPAIGN_CARD_IDENTIFIER = "campaign-card";

const DESKTOP_PRODUCTS_PER_ROW = 4;
const TABLET_PRODUCTS_PER_ROW = 3;
const MOBILE_PRODUCTS_PER_ROW = 2;
const TINY_BREAKPOINT = 550;
const MEDIUM_BREAKPOINT = 1057;

const isProductsPerRow = (productsPerRow: any): boolean => {
  return productsPerRow === MOBILE_PRODUCTS_PER_ROW ||
    productsPerRow === TABLET_PRODUCTS_PER_ROW ||
    productsPerRow === DESKTOP_PRODUCTS_PER_ROW;
};

const getActiveProductsPerRow = (requestedProductsPerRow?: ?number): number => {
  if (isProductsPerRow(requestedProductsPerRow)) {
    return (requestedProductsPerRow: any);
  }

  if (typeof window === "undefined") {
    return DESKTOP_PRODUCTS_PER_ROW;
  }

  if (window.innerWidth <= TINY_BREAKPOINT) {
    return MOBILE_PRODUCTS_PER_ROW;
  }

  if (window.innerWidth <= MEDIUM_BREAKPOINT) {
    return TABLET_PRODUCTS_PER_ROW;
  }

  return DESKTOP_PRODUCTS_PER_ROW;
};

const getCampaignSlotCount = (productsPerRow: number): number => {
  return productsPerRow === MOBILE_PRODUCTS_PER_ROW ? 2 : 1;
};

const getVisibleProductCount = (productsPerRow: number, page: number): number => {
  const visibleSlots = PAGE_SIZE - (PAGE_SIZE % productsPerRow);

  return page === 1 ?
    visibleSlots - getCampaignSlotCount(productsPerRow) :
    visibleSlots;
};

const getProductStartIndex = (productsPerRow: number, page: number): number => {
  if (page === 1) {
    return 0;
  }

  return getVisibleProductCount(productsPerRow, 1) +
    ((page - 2) * getVisibleProductCount(productsPerRow, 2));
};

const getProductWindow = (
  page: number,
  productsPerRow: number
): { endIndex: number, startIndex: number } => {
  const startIndex = getProductStartIndex(productsPerRow, page);
  const endIndex = startIndex + getVisibleProductCount(productsPerRow, page);

  return {
    startIndex,
    endIndex,
  };
};

const getFirstLinkPath = (html: string): ?string => {
  const match = html.match(/<a\s[^>]*href=(["'])(.*?)\1/i);

  return match && match[2] ? match[2].split(/[?#]/)[0] : null;
};

const normalizePath = (path: ?string): ?string => {
  if (!path) {
    return null;
  }

  const pathWithSlash = path.startsWith("/") ? path : "/" + path;

  return pathWithSlash.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, "") || "/";
};

const isSamePath = (a: ?string, b: ?string): boolean => {
  return Boolean(a) && Boolean(b) && (
    a === b ||
    normalizePath(a) === normalizePath(b)
  );
};

const hasActiveCampaignCard = async (client: any, pathname: string): Promise<boolean> => {
  const { block } = await client(cmsBlock, {
    identifier: CAMPAIGN_CARD_IDENTIFIER,
  }, { cache: true });

  if (!block || !block.content) {
    return false;
  }

  return !isSamePath(getFirstLinkPath(block.content), pathname);
};

const getIn = (value: any, path: string): any => {
  return path.split(".").reduce((acc, key) => acc && acc[key], value);
};

const setIn = (value: any, path: string, replacement: any): any => {
  const [key, ...rest] = path.split(".");

  if (rest.length === 0) {
    return {
      ...value,
      [key]: replacement,
    };
  }

  return {
    ...value,
    [key]: setIn(value[key], rest.join("."), replacement),
  };
};

export const loadCampaignAwareProductList = async (
  {
    client,
    query,
    variables,
    productListPath,
    pathname,
    productsPerRow: requestedProductsPerRow,
  }: {
    client: any,
    query: any,
    variables: any,
    productListPath: string,
    pathname: string,
    productsPerRow?: ?number,
  }
): Promise<any> => {
  const page = typeof variables.page === "number" && variables.page > 0 ? variables.page : 1;
  const activeCampaignCard = await hasActiveCampaignCard(client, pathname);
  const currentVariables = {
    ...variables,
    page,
    pageSize: PAGE_SIZE,
  };

  if (page === 1 || !activeCampaignCard) {
    return client(query, currentVariables, { cache: true });
  }

  const productsPerRow = getActiveProductsPerRow(requestedProductsPerRow);
  const { startIndex, endIndex } = getProductWindow(page, productsPerRow);
  const firstPageToFetch = Math.floor(startIndex / PAGE_SIZE) + 1;
  const lastPageToFetch = Math.ceil(endIndex / PAGE_SIZE);
  const firstPageStartIndex = (firstPageToFetch - 1) * PAGE_SIZE;
  const pageNumbers = Array.from(
    { length: lastPageToFetch - firstPageToFetch + 1 },
    (_, index) => firstPageToFetch + index
  );
  const productPages = [];

  await pageNumbers.reduce((previous, productPage) => {
    return previous.then(async () => {
      productPages.push(await client(query, {
        ...currentVariables,
        page: productPage,
      }, { cache: true }));
    });
  }, Promise.resolve());

  const currentData = productPages[0];
  const currentList = getIn(currentData, productListPath);

  if (!currentList || !currentList.items) {
    return currentData;
  }

  const productWindow = productPages.flatMap(productPage => {
    const productList = getIn(productPage, productListPath);

    return productList && productList.items ? productList.items : [];
  }).slice(startIndex - firstPageStartIndex, endIndex - firstPageStartIndex);

  return setIn(
    currentData,
    productListPath,
    {
      ...currentList,
      items: productWindow,
      campaignAwareWindowProductsPerRow: productsPerRow,
    }
  );
};
